import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { EBOOK_SUBMISSION_ITEM_STATUS_LABEL } from '../../app/ebookSubmissions/constants/status';
import EbookSubmissionDetailPresenter from '../../app/ebookSubmissions/presenters/EbookSubmissionDetailPresenter';
import EbookSubmissionItemsTablePresenter from '../../app/ebookSubmissions/presenters/EbookSubmissionItemsTablePresenter';
import ConfirmModal from '../../app/modals/ConfirmModal';
import Breadcrumbs from '../../components/Breadcrumbs';
import Button from '../../components/Button';
import LoadingModal from '../../components/modal/LoadingModal';
import ToastComponent, { IToastType } from '../../components/Toast/ToastComponent';
import { PintoEbookSubmissionItemStatus, PintoEbookSubmissionItemsFilter, PintoEbookSubmissionStatus } from '../../core/graphql/types';
import useMutationCancelEbookSubmission from '../../hooks/useMutationCancelEbookSubmission';
import useMutationCancelEbookSubmissionItem from '../../hooks/useMutationCancelEbookSubmissionItem';
import useQueryEbookSubmission from '../../hooks/useQueryEbookSubmission';
import useQueryEbookSubmissionItems from '../../hooks/useQueryEbookSubmissionItems';

const ITEMS_PAGE_LIMIT = 10;

const ItemStatusOptions = [
  { label: 'All', value: null },
  { label: EBOOK_SUBMISSION_ITEM_STATUS_LABEL[PintoEbookSubmissionItemStatus.Pending], value: PintoEbookSubmissionItemStatus.Pending },
  { label: EBOOK_SUBMISSION_ITEM_STATUS_LABEL[PintoEbookSubmissionItemStatus.Processing], value: PintoEbookSubmissionItemStatus.Processing },
  { label: EBOOK_SUBMISSION_ITEM_STATUS_LABEL[PintoEbookSubmissionItemStatus.Completed], value: PintoEbookSubmissionItemStatus.Completed },
  { label: EBOOK_SUBMISSION_ITEM_STATUS_LABEL[PintoEbookSubmissionItemStatus.Cancelled], value: PintoEbookSubmissionItemStatus.Cancelled },
  { label: EBOOK_SUBMISSION_ITEM_STATUS_LABEL[PintoEbookSubmissionItemStatus.Failed], value: PintoEbookSubmissionItemStatus.Failed },
];

function EbookSubmissionDetailPage() {
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const id = searchParams.get('id');

  const { fetchEbookSubmission, ebookSubmission, isLoadingEbookSubmission } = useQueryEbookSubmission();
  const { fetchEbookSubmissionItems, ebookSubmissionItems, isLoadingEbookSubmissionItems } = useQueryEbookSubmissionItems();
  const { cancelEbookSubmission, isCancellingEbookSubmission } = useMutationCancelEbookSubmission();
  const { cancelEbookSubmissionItem, isCancellingEbookSubmissionItem } = useMutationCancelEbookSubmissionItem();

  const [currentItemsPage, setCurrentItemsPage] = useState<number>(1);
  const [itemsStatus, setItemsStatus] = useState<PintoEbookSubmissionItemStatus | null>(null);
  const [showConfirmCancelSubmission, setShowConfirmCancelSubmission] = useState<boolean>(false);
  const [showConfirmCancelItem, setShowConfirmCancelItem] = useState<boolean>(false);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(undefined);

  const currentItemsFilter: PintoEbookSubmissionItemsFilter = useMemo(
    () => ({
      limit: ITEMS_PAGE_LIMIT,
      offset: (currentItemsPage - 1) * ITEMS_PAGE_LIMIT,
      anyStatuses: itemsStatus ? [itemsStatus] : undefined,
    }),
    [currentItemsPage, itemsStatus],
  );

  useEffect(() => {
    if (id) {
      fetchEbookSubmission(id);
    }
  }, [id, fetchEbookSubmission]);

  useEffect(() => {
    if (id) {
      setCurrentItemsPage(1);
      fetchEbookSubmissionItems(id, { limit: ITEMS_PAGE_LIMIT, offset: 0, anyStatuses: itemsStatus ? [itemsStatus] : undefined });
    }
  }, [id, itemsStatus, fetchEbookSubmissionItems]);

  const onChangeItemsPage = useCallback(
    (page: number) => {
      setCurrentItemsPage(page);
      if (id) {
        fetchEbookSubmissionItems(id, {
          limit: ITEMS_PAGE_LIMIT,
          offset: (page - 1) * ITEMS_PAGE_LIMIT,
          anyStatuses: itemsStatus ? [itemsStatus] : undefined,
        });
      }
    },
    [id, itemsStatus, fetchEbookSubmissionItems],
  );

  const handleSetItemsStatus = useCallback((status: PintoEbookSubmissionItemStatus | null) => {
    setItemsStatus(status);
  }, []);

  const onConfirmCancelSubmission = useCallback(async () => {
    setShowConfirmCancelSubmission(false);
    if (!id) return;

    const { success } = await cancelEbookSubmission(id);
    if (success) {
      ToastComponent({ label: 'Ebook submission cancelled', type: IToastType.SUCCESS });
      fetchEbookSubmission(id);
    }
  }, [id, cancelEbookSubmission, fetchEbookSubmission]);

  const onConfirmCancelItem = useCallback(async () => {
    setShowConfirmCancelItem(false);
    if (!selectedItemId || !id) return;

    const { success } = await cancelEbookSubmissionItem(selectedItemId);
    if (success) {
      ToastComponent({ label: 'Item cancelled', type: IToastType.SUCCESS });
      fetchEbookSubmission(id);
      fetchEbookSubmissionItems(id, currentItemsFilter);
    }
    setSelectedItemId(undefined);
  }, [selectedItemId, id, cancelEbookSubmissionItem, fetchEbookSubmission, fetchEbookSubmissionItems, currentItemsFilter]);

  const onCancelItem = useCallback((itemId: string) => {
    setSelectedItemId(itemId);
    setShowConfirmCancelItem(true);
  }, []);

  const items = useMemo(() => ebookSubmissionItems?.edges?.map((edge) => edge.node) || [], [ebookSubmissionItems]);

  if (isLoadingEbookSubmission && !ebookSubmission) {
    return (
      <div className="space-y-12">
        <div className="w-full btn btn-outline btn-disabled bg-systemGrays04LabelQuaternary border-none loading h-[100px]" />
      </div>
    );
  }

  if (!isLoadingEbookSubmission && !ebookSubmission) {
    return (
      <div className="space-y-12">
        <Breadcrumbs items={[{ name: 'Ebook Submissions', url: '/ebookSubmissions' }, { name: 'Not found' }]} />
        <div className="flex flex-col items-center justify-center py-48 space-y-12">
          <div className="font-dbh text-20">Ebook submission not found</div>
          <div className="text-colorsBrandWarmBlack02LabelSecondary">
            {`We couldn't find a submission with id "${id || ''}". It may have been removed, or the link is incorrect.`}
          </div>
          <Link to="/ebookSubmissions">
            <Button type="outline" color="gray">
              Back to Ebook Submissions
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      <div className="sticky top-0 z-50 px-5 bg-componentsBgGrouped02">
        {/* section: breadcrumbs */}
        <Breadcrumbs items={[{ name: 'Ebook Submissions', url: '/ebookSubmissions' }, { name: ebookSubmission?.fileName || 'Ebook Submission Detail' }]} />
        {/* section: title */}
        <div className="flex flex-col justify-between pt-5 md:flex-row">
          <div className="w-[70%] h-full">
            <h1 className="font-dbh text-[28px] break-words">{ebookSubmission?.fileName || 'Ebook Submission Detail'}</h1>
          </div>

          <div className="flex items-center space-x-12">
            <Link to="/ebookSubmissions">
              <Button type="outline" color="gray" className="space-x-4">
                Back
              </Button>
            </Link>

            <Button
              disabled={ebookSubmission?.status !== PintoEbookSubmissionStatus.Pending}
              onClick={() => setShowConfirmCancelSubmission(true)}
              type="outline"
              color="red"
              className="space-x-4"
            >
              Cancel submission
            </Button>
          </div>
        </div>

        {/* section: main */}
        <div className="mb-0 divider opacity-10"></div>
      </div>

      <EbookSubmissionDetailPresenter ebookSubmission={ebookSubmission} />

      <EbookSubmissionItemsTablePresenter
        ebookSubmissionItems={items}
        currentPage={currentItemsPage}
        totalCount={ebookSubmissionItems?.totalCount || 0}
        isLoading={isLoadingEbookSubmissionItems}
        onChangePage={onChangeItemsPage}
        onCancel={onCancelItem}
        status={itemsStatus}
        statusOptions={ItemStatusOptions}
        onSetStatus={handleSetItemsStatus}
        itemStatusCounts={ebookSubmission?.itemStatusCounts}
      />

      {(isCancellingEbookSubmission || isCancellingEbookSubmissionItem) && <LoadingModal />}

      {showConfirmCancelSubmission && (
        <ConfirmModal
          onClose={() => setShowConfirmCancelSubmission(false)}
          onConFirm={onConfirmCancelSubmission}
          title="Cancel submission"
          subTitle={`Are you sure you want to cancel this submission? All pending items in "${ebookSubmission?.fileName}" will stop processing. This cannot be undone.`}
          cancelText="Keep it"
          confirmText="Cancel submission"
        />
      )}

      {showConfirmCancelItem && (
        <ConfirmModal
          onClose={() => {
            setShowConfirmCancelItem(false);
            setSelectedItemId(undefined);
          }}
          onConFirm={onConfirmCancelItem}
          title="Cancel item"
          subTitle="Are you sure you want to cancel this item? It will stop processing and won't be published."
          cancelText="Cancel"
          confirmText="Confirm"
        />
      )}
    </div>
  );
}

export default EbookSubmissionDetailPage;
